import { useLayoutEffect, useState } from "react";

/**
 * Height that makes the element behind `ref` fill the space between its top and
 * the footer, so the page itself does not scroll and whatever is inside (e.g.
 * FullCalendar with height="100%") scrolls internally instead.
 *
 * Re-measured on window resize and whenever content above the element changes
 * height. Returns null until the first measurement.
 */
export default function useFitToViewport(
    ref,
    { bottomGap = 16, minHeight = 400 } = {},
) {
    const [height, setHeight] = useState(null);

    useLayoutEffect(() => {
        const measure = () => {
            const el = ref.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top + window.scrollY;
            const footerH =
                document.querySelector(".main-footer")?.offsetHeight || 0;
            setHeight(
                Math.max(
                    Math.floor(window.innerHeight - top - footerH - bottomGap),
                    minHeight,
                ),
            );
        };
        measure();
        window.addEventListener("resize", measure);
        // Deferred a frame so the height change is not applied inside the observer
        // callback (avoids "ResizeObserver loop" warnings).
        let frame = null;
        const ro =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(() => {
                      cancelAnimationFrame(frame);
                      frame = requestAnimationFrame(measure);
                  })
                : null;
        ro?.observe(document.body);
        return () => {
            window.removeEventListener("resize", measure);
            ro?.disconnect();
            cancelAnimationFrame(frame);
        };
    }, [ref, bottomGap, minHeight]);

    return height;
}
